import * as React from "react";
import { TICKET_MESSAGE_MAX_LENGTH } from "@/constants/ticket";
import { Box } from "@mui/material";
import ChatboxInputButton from "./button";
import { useTranslation } from "react-i18next";
import { useUploader } from "@/hook";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IconButton } from "@mui/material";
import GalleryItem from "../../gallery-uploader/item";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

interface IMessageTextFieldProps {
  handleChange: Function;
  addMessage: Function;
  val: string;
}

const MessageTextField: React.FunctionComponent<IMessageTextFieldProps> = ({
  handleChange,
  addMessage,
  val,
}) => {
  const [file, setFile] = React.useState<null | File>(null);
  const [text, setText] = React.useState("");

  const [showAttach, setShowAttach] = React.useState(false);
  const [removeAttach, setRemoveAttach] = React.useState(false);

  const HiddenAttachment = () => {
    setShowAttach(false);
  };
  const ShowAttachment = () => {
    setShowAttach(true);
  };
  const HandleRemove = () => {
    setRemoveAttach(!removeAttach);
  };

  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleChangeFile = React.useCallback((file: File) => {
    setFile(file);
  }, []);

  const { t } = useTranslation(["common"]);

  const handleAddingMessage = () => {
    addMessage(text, file);
    setFile(null);
    setText("");
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newVal = e.target.value.slice(0, TICKET_MESSAGE_MAX_LENGTH);
    handleChange(newVal);
    setText(newVal);

    // Adjust the height of the textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // Reset the height to auto to shrink if necessary
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Set to scrollHeight
    }
  };

  return (
    <Box
      sx={{
        borderTop: "1px solid #ddd",
      }}
    >

      {file && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            padding: "6px 10px",
            backgroundColor: "#fafafa",
            fontSize: "14px",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <span style={{ fontSize: "16px" }}>📄</span>
          <span
            style={{
              flexGrow: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: "pointer",
            }}
            onClick={() => ShowAttachment()}
          >
            {file.name}
          </span>
          <IconButton
            onClick={() => {
              HandleRemove();
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          position: "relative",
          [`& textarea`]: {
            border: 0,
            width: "100%",
            outline: 0,
            p: 2,
            fontSize: 18,
          },
        }}
      >
        <textarea
          ref={textareaRef}
          value={text}
          maxLength={TICKET_MESSAGE_MAX_LENGTH}
          onChange={handleTextChange}
          placeholder={t("common:enter_your_message_or_request_text")}
          style={{
            width: "100%",
            height: "auto",
            minHeight: "50px",
            resize: "none",
            padding: "8px",
            fontSize: "16px",
            fontFamily: "Vazir",
            borderRadius: "4px",
            overflow: "hidden", // Prevent scrollbar display
          }}
        />
        <Box sx={{ px: 2, color: "text.secondary", fontSize: 12 }}>
          {TICKET_MESSAGE_MAX_LENGTH}/{text.length}
        </Box>
        <ChatboxInputButton
          removeAttach={removeAttach}
          handleChangeFile={handleChangeFile}
          text={text}
          addMessage={handleAddingMessage}
        />
      </Box>

      <Dialog
        open={showAttach}
        onClose={HiddenAttachment}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        role="alertdialog"
        maxWidth={"xs"}
        PaperProps={{
          sx: {
            p: 0,
          },
        }}
      >
        <GalleryItem
          item={file}
          handleRemove={HandleRemove}
          showActions={false}
        />
      </Dialog>

    </Box>
  );
};

export default React.memo(MessageTextField);
